<script setup>
import {computed, inject, ref} from "vue";
import {router, useForm} from "@inertiajs/vue3"
import {useCan} from "@/utils/permissions";
import FormButton from "@/components/Shared/Form/Button"
import FormInput from "@/components/Shared/Form/Input/Index"
import UpdateBillingAddressModal from "@/components/Dashboard/UpdateBillingAddressModal.vue";

const trans = inject('trans');
const can = useCan()

const props = defineProps({
  user: Object
})

const isUpdateBillingAddressModalOpen = ref(false)
const hasValidBillingAddress = ref(props.user.has_valid_billing_address)

const form = useForm({
  billing_cc_invoices_to: props.user.billing_cc_invoices_to,
  vat_id: props.user.vat_id
})

const canUpdate = computed(() => can('billings.update'))

const submit = () => {
  if (!canUpdate.value) {
    return
  }

  form.put(route('dashboard.account.billing.payment_information.update'), {preserveScroll: true})
}

const updatePaymentMethod = () => {
  if (!canUpdate.value) {
    return
  }

  if (!hasValidBillingAddress.value) {
    isUpdateBillingAddressModalOpen.value = true

    return
  }

  router.visit(
    route('dashboard.account.billing.payment_method.update'),
    {
      method: 'put'
    }
  )
}
</script>
<template>
  <div id="billing_payment-details" class="mb-8">
    <div class="card tile">
      <div class="card-header border-bottom rounded-0">
        <h3 class="card-text">
          <span>{{ trans('dashboard.account.billing.payment_information.title') }}</span>
        </h3>
      </div>
      <div class="card-body">
        <div id="billing_update-payment-method" class="row mb-3 align-items-center">
          <div v-if="!user.valid_mollie_mandate" class="col-md-12">
            <div class="alert alert-warning">
              {{ trans('dashboard.account.billing.payment_information.notice') }}
            </div>
          </div>
          <div class="col-md-6">
            <div v-if="user.card_last_four" class="flex align-items-center">
              <div v-if="user.card_brand" class="inline-flex" style="height: 60px;">
                <img v-if="user.card_brand.startsWith('BIC')"
                     :src="'/images/payment-methods/ideal_payment_icon.svg'"
                     :alt="user.card_brand"
                     class="img-fluid">
                <img v-else
                     :src="'/images/payment-methods/'+user.card_brand+'_payment_icon.svg'"
                     :alt="user.card_brand"
                     class="img-fluid">
              </div>
              <div v-if="user.card_last_four" class="inline-flex">
                <div class="mx-2 pt-1 text-80">
                  <span>****</span>
                  <span class="mx-2">****</span>
                  <span>****</span>
                </div>
                <h4>{{ user.card_last_four }}</h4>
              </div>
            </div>
            <div v-else>
              <p>{{ trans('dashboard.account.billing.payment_information.no_payment_method') }}</p>
            </div>
          </div>
          <div class="col-md-6 text-right">
            <form role="form" @submit.prevent="updatePaymentMethod" aria-label="Set payment method">
              <div>
                <span></span>
                <form-button v-if="canUpdate"
                             :value="trans('dashboard.account.billing.payment_information.update_payment_method')"
                             :processing-value="trans('common.redirecting')"/>
              </div>
            </form>
          </div>
        </div>

        <div class="my-2">
          <form role="form" @submit.prevent="submit" aria-label="Update billing information">
            <form-input
              id="billing-sending_invoices_to"
              name="billing_sending_invoices_to"
              type="text"
              class="disabled"
              v-model="user.email"
              :label-text="trans('dashboard.account.billing.payment_information.sending_invoices_to')"
              disabled/>

            <form-input
              id="billing-cc_invoices_to"
              name="billing_cc_invoices_to"
              type="text"
              v-model="form.billing_cc_invoices_to"
              :label-text="trans('dashboard.account.billing.payment_information.cc_invoices_to')"
              :error="form.errors.billing_cc_invoices_to"/>

            <form-input
              id="billing-vat_id"
              name="vat_id"
              v-model="form.vat_id"
              :label-text="trans('dashboard.account.billing.payment_information.vat_id_number')"
              :error="form.errors.vat_id"/>

            <div class="form_row">
              <span></span>
              <form-button v-if="canUpdate"
                           :value="trans('common.update')"
                           :processing="form.processing"/>
            </div>
          </form>
        </div>
      </div>
      <div class="card-footer">
        <label class="col-form-label">
          * {{ trans('dashboard.account.billing.payment_information.footnote') }}
        </label>
      </div>
    </div>
  </div>
  <update-billing-address-modal
    v-if="isUpdateBillingAddressModalOpen"
    :user="user"
    @close="() => (isUpdateBillingAddressModalOpen = false)"
    @valid="() => (hasValidBillingAddress = true)"
  />
</template>
